import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import DashboardLayout from "../../components/DashboardLayout";
import IdeaQueueDeck from "../../components/ideas/IdeaQueueDeck";
import IdeaRejectModal from "../../components/ideas/IdeaRejectModal";
import PastIdeasRail from "../../components/ideas/PastIdeasRail";
import IdeaDetailModal from "../../components/modals/IdeaDetailModal";
import IdeasPopulatingModal from "./IdeasPopulatingModal";
import { brandsService } from "../../services/brands";
import { useToast } from "../../contexts/ToastContext";
import { getApiErrorMessage } from "../../utils/apiErrors";
import { parsePaginatedResponse } from "../../utils/pagination";
import { scriptDetailPath } from "../../utils/scriptRoutes";

const QUEUE_PAGE_SIZE = 5;
const PAST_PAGE_SIZE = 30;

const Ideas = ({ currentUser }) => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { currentBrand } = useSelector((state) => state.brand);

  const [queueIdeas, setQueueIdeas] = useState([]);
  const [queueCount, setQueueCount] = useState(0);
  const [reviewedAllIdeas, setReviewedAllIdeas] = useState(false);
  const [pastIdeas, setPastIdeas] = useState([]);
  const [queueLoading, setQueueLoading] = useState(true);
  const [pastLoading, setPastLoading] = useState(false);
  const [queueError, setQueueError] = useState(null);
  const [pastError, setPastError] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [generatingScriptForIdea, setGeneratingScriptForIdea] = useState(null);
  const [rejectingIdea, setRejectingIdea] = useState(null);
  const [submittingReject, setSubmittingReject] = useState(false);
  const [highlightedPastHash, setHighlightedPastHash] = useState(null);
  const [exitTrigger, setExitTrigger] = useState(null);
  const [refreshToken, setRefreshToken] = useState(0);
  const pendingPastIdeaRef = useRef(null);
  const pendingScriptHashRef = useRef(null);
  const reviewedQueueHashesRef = useRef(new Set());

  const loadQueue = useCallback((showLoading = true) => {
    if (!currentBrand?.hash) return;
    if (showLoading) setQueueLoading(true);
    setQueueError(null);
    const reviewedHashes = Array.from(reviewedQueueHashesRef.current);
    const params = {
      page: 1,
      page_size: QUEUE_PAGE_SIZE,
      brand_hash: currentBrand.hash,
      queue_status: "queued",
    };
    if (reviewedHashes.length > 0) {
      // ponytail: queue sessions are small; use cursor pagination if this approaches URL limits.
      params.exclude_idea_hashes = reviewedHashes.join(",");
    }
    brandsService.listIdeas(
      params,
      (response) => {
        const { results, count } = parsePaginatedResponse(response);
        setQueueIdeas(results);
        if (reviewedHashes.length === 0) setQueueCount(count);
        setReviewedAllIdeas(results.length === 0 && reviewedHashes.length > 0);
        setQueueLoading(false);
      },
      () => {
        setQueueError("Could not load your idea queue.");
        setQueueLoading(false);
      },
    );
  }, [currentBrand?.hash]);

  const loadPastIdeas = useCallback(() => {
    if (!currentBrand?.hash) return;
    setPastLoading(true);
    setPastError(null);
    brandsService.listIdeas(
      {
        page: 1,
        page_size: PAST_PAGE_SIZE,
        brand_hash: currentBrand.hash,
        queue_status: "converted,dismissed",
      },
      (response) => {
        const { results } = parsePaginatedResponse(response);
        setPastIdeas(results);
        setPastLoading(false);
      },
      () => {
        setPastError("Could not load past ideas.");
        setPastLoading(false);
      },
    );
  }, [currentBrand?.hash]);

  useEffect(() => {
    reviewedQueueHashesRef.current.clear();
    setReviewedAllIdeas(false);
    loadQueue();
    loadPastIdeas();
  }, [loadQueue, loadPastIdeas, refreshToken]);

  const prependPastIdea = useCallback((idea) => {
    if (!idea?.hash) return;
    setPastIdeas((prev) => [idea, ...prev.filter((item) => item.hash !== idea.hash)]);
    setHighlightedPastHash(idea.hash);
    window.setTimeout(() => setHighlightedPastHash(null), 1800);
  }, []);

  const removeFromQueue = useCallback((ideaHash) => {
    setQueueIdeas((prev) => prev.filter((item) => item.hash !== ideaHash));
  }, []);

  const handleExitSettled = useCallback(
    (ideaHash, mode) => {
      reviewedQueueHashesRef.current.add(ideaHash);
      if (mode !== "skip" && pendingPastIdeaRef.current?.hash === ideaHash) {
        prependPastIdea(pendingPastIdeaRef.current);
        pendingPastIdeaRef.current = null;
      }
      removeFromQueue(ideaHash);
      if (mode !== "skip") {
        setQueueCount((count) => Math.max(0, count - 1));
      }
      if (queueIdeas.length <= 1) {
        loadQueue();
      }
      if (pendingScriptHashRef.current) {
        navigate(scriptDetailPath(pendingScriptHashRef.current));
        pendingScriptHashRef.current = null;
      }
      setExitTrigger(null);
    },
    [loadQueue, navigate, prependPastIdea, queueIdeas.length, removeFromQueue],
  );

  const handleReviewAgain = useCallback(() => {
    reviewedQueueHashesRef.current.clear();
    setReviewedAllIdeas(false);
    loadQueue();
  }, [loadQueue]);

  const handleGenerate = () => {
    if (!currentBrand?.hash || generating) return;
    setGenerating(true);
    brandsService.createIdeaRun(
      { brand_hash: currentBrand.hash, count: 3 },
      (response) => {
        setGenerating(false);
        const runHash = response?.data?.hash;
        if (runHash) {
          navigate(`/ideas/populating/${runHash}/`);
        }
      },
      () => {
        setGenerating(false);
        showToast("Could not start idea generation.", { type: "error" });
      },
    );
  };

  const handleCreateScript = (idea) => {
    if (!idea?.hash) return;
    setGeneratingScriptForIdea(idea.hash);
    brandsService.createScript(
      { idea_hash: idea.hash },
      (response) => {
        const script = response?.data;
        const convertedIdea = {
          ...idea,
          queue_status: "converted",
          resolved_at: new Date().toISOString(),
          scripts: [script, ...(Array.isArray(idea.scripts) ? idea.scripts : [])],
        };
        pendingPastIdeaRef.current = convertedIdea;
        if (script?.hash) {
          pendingScriptHashRef.current = script.hash;
        }
        setExitTrigger({ hash: idea.hash, mode: "convert", key: Date.now() });
        setGeneratingScriptForIdea(null);
      },
      (error) => {
        setGeneratingScriptForIdea(null);
        showToast(getApiErrorMessage(error, "Could not create script. Please try again."), {
          type: "error",
        });
      },
    );
  };

  const handleRejectSubmit = (feedback) => {
    if (!rejectingIdea?.hash) return;
    setSubmittingReject(true);
    brandsService.updateIdea(
      rejectingIdea.hash,
      { dismiss: true, rejection_feedback: feedback },
      (response) => {
        const updated = response?.data || {
          ...rejectingIdea,
          queue_status: "dismissed",
          rejection_feedback: feedback,
        };
        pendingPastIdeaRef.current = updated;
        setExitTrigger({ hash: rejectingIdea.hash, mode: "reject", key: Date.now() });
        setSubmittingReject(false);
        setRejectingIdea(null);
      },
      (error) => {
        setSubmittingReject(false);
        showToast(getApiErrorMessage(error, "Could not reject this idea."), { type: "error" });
      },
    );
  };

  const handleOpenPastIdea = (idea) => {
    if (!idea?.hash) return;
    navigate(`/ideas/${encodeURIComponent(idea.hash)}/overview/`);
  };

  const handleRejectFromModal = (idea) => {
    navigate("/ideas/");
    setRejectingIdea(idea);
  };

  const handleSkipFromModal = (idea) => {
    const currentIndex = queueIdeas.findIndex((item) => item.hash === idea?.hash);
    if (currentIndex < 0) return;
    const nextIdea = queueIdeas[(currentIndex + 1) % queueIdeas.length];
    setExitTrigger({ hash: idea.hash, mode: "skip", key: Date.now() });
    if (nextIdea && nextIdea.hash !== idea.hash) {
      navigate(`/ideas/${encodeURIComponent(nextIdea.hash)}/overview/`);
    } else {
      navigate("/ideas/");
    }
  };

  const handlePopulatingComplete = useMemo(
    () => () => setRefreshToken((value) => value + 1),
    [],
  );

  if (!currentBrand?.hash) {
    return (
      <DashboardLayout title="Ideas" currentUser={currentUser}>
        <div className="rounded-lg border border-theme-secondary bg-theme-secondary p-6 text-theme-text opacity-80">
          Select a brand to view your idea queue.
        </div>
      </DashboardLayout>
    );
  }

  const primaryAction = (
    <button
      type="button"
      onClick={handleGenerate}
      disabled={generating}
      className="inline-flex items-center gap-2 px-4 py-2 bg-theme-primary text-theme-button text-sm font-semibold rounded-md hover:opacity-90 disabled:opacity-50"
    >
      {generating ? "Starting..." : "Generate more ideas"}
    </button>
  );

  return (
    <DashboardLayout title="Ideas" currentUser={currentUser} primaryAction={primaryAction}>
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-3 min-w-0">
          <IdeaQueueDeck
            ideas={queueIdeas}
            totalCount={queueCount}
            reviewedAll={reviewedAllIdeas}
            loading={queueLoading}
            error={queueError}
            onGenerate={handleGenerate}
            onCreateScript={handleCreateScript}
            onReject={setRejectingIdea}
            onReviewAgain={handleReviewAgain}
            isGeneratingScript={Boolean(generatingScriptForIdea)}
            exitTrigger={exitTrigger}
            onExitSettled={handleExitSettled}
          />
        </div>
        <div className="lg:col-span-1">
          <PastIdeasRail
            ideas={pastIdeas}
            loading={pastLoading}
            error={pastError}
            highlightedHash={highlightedPastHash}
            onOpenIdea={handleOpenPastIdea}
          />
        </div>
      </div>

      {rejectingIdea ? (
        <IdeaRejectModal
          idea={rejectingIdea}
          onClose={() => !submittingReject && setRejectingIdea(null)}
          onSubmit={handleRejectSubmit}
          submitting={submittingReject}
        />
      ) : null}

      <Routes>
        <Route
          path="populating/:runHash"
          element={<IdeasPopulatingModal onComplete={handlePopulatingComplete} />}
        />
        <Route
          path="populating/:runHash/*"
          element={<IdeasPopulatingModal onComplete={handlePopulatingComplete} />}
        />
        <Route
          path=":ideaHash/:tab"
          element={
            <IdeaDetailModal
              onClose={() => navigate("/ideas/")}
              onCreateScript={handleCreateScript}
              onReject={handleRejectFromModal}
              onSkip={handleSkipFromModal}
              isGeneratingScript={Boolean(generatingScriptForIdea)}
            />
          }
        />
        <Route path=":ideaHash" element={<Navigate to="overview/" replace />} />
      </Routes>
    </DashboardLayout>
  );
};

export default Ideas;
